import { useEffect, useState } from "react";
import { Header } from "../components/Header";
import { AIInsightBanner } from "../components/AIInsightBanner";
import { PageTimeline } from "../components/PageTimeline";
import { KpiCard } from "../components/KpiCard";
import { usePeriod } from "../lib/PeriodContext";
import api from "../lib/api";
import { fmtINR, fmtPct } from "../lib/format";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { ResponsiveContainer } from "../components/AutoSizer";
import { useAuth } from "../lib/AuthContext";

export default function TargetVsAchievement() {
  const { asOf, projectsQS } = usePeriod();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [edit, setEdit] = useState({});
  const [draft, setDraft] = useState({});

  const canEdit = user?.role === "admin" || user?.role === "manager";

  useEffect(() => {
    if (!asOf) return;
    api.get(`/kpi/targets?as_of=${asOf}${projectsQS}`).then((r) => setData(r.data));
    loadMatrix();
  }, [asOf, projectsQS]);

  const loadMatrix = async () => {
    const [tgts, proj] = await Promise.all([
      api.get("/targets/manage?kind=sales"),
      api.get("/projects/all"),
    ]);
    setMatrix({ targets: tgts.data.targets, projects: proj.data.projects });
  };

  const monthsList = matrix ? [...new Set(matrix.targets.map((t) => t.period))].sort() : [];

  const saveCell = async (pname, periodStr) => {
    const key = `${pname}|${periodStr}`;
    const val = parseFloat(draft[key]);
    const cnt = parseInt(draft[key + "_c"] || 0, 10);
    if (isNaN(val)) { toast.error("Enter valid number"); return; }
    try {
      await api.post("/targets/manage", {
        project_name: pname, period: periodStr, kind: "sales",
        target_value: val, target_count: cnt,
      });
      toast.success("Saved");
      setEdit({ ...edit, [key]: false });
      loadMatrix();
      // Refresh KPI summary
      api.get(`/kpi/targets?as_of=${asOf}${projectsQS}`).then((r) => setData(r.data));
    } catch (e) {
      toast.error("Save failed");
    }
  };

  if (!data) return <><Header title="Target vs Achievement" /><div className="p-6">Loading...</div></>;

  const sel = data.selected || {};

  return (
    <>
      <Header title="Target vs Achievement" subtitle={`${data.fy_label} · Carry-forward Active`} />
      <div className="p-3 sm:p-6 md:p-8 max-w-[1600px] mx-auto space-y-5 min-w-0">
        <PageTimeline fyLabel={data.fy_label} />
        <AIInsightBanner context={{ fy_label: data.fy_label, kpis: { sales_target: data.fy_summary.target, net_sales: data.fy_summary.actual } }} />

        <div className="flex items-center gap-3 flex-wrap">
          <div className="px-3 py-1.5 bg-vexen-sidebar text-white rounded-md text-xs font-mono uppercase tracking-wider">
            {data.selected_label}
          </div>
          <div className="text-xs text-slate-600 font-mono">
            FY Achievement:&nbsp;
            <span className="font-semibold text-slate-900">{fmtPct(data.fy_summary.achievement_pct)}</span>
            &nbsp;· {fmtINR(data.fy_summary.actual, { short: true })} of {fmtINR(data.fy_summary.target, { short: true })}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KpiCard label="Sales Target" value={fmtINR(sel.base_target || 0, { short: true })} testId="kpi-base-target" />
          <KpiCard label="Carry-in Deficit" value={fmtINR(sel.carry_in || 0, { short: true })} testId="kpi-carry-in" />
          <KpiCard label="Effective Target" value={fmtINR(sel.effective_target || 0, { short: true })} dark testId="kpi-effective" />
          <KpiCard label="Net Actual" value={fmtINR(sel.actual || 0, { short: true })} testId="kpi-net-actual" />
          <KpiCard label="Achievement" value={fmtPct(sel.achievement_pct || 0)} accent testId="kpi-achievement" />
        </div>

        <div className="bg-white rounded-md border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold">Collection Target vs Actual</div>
            <div className="text-xs font-mono text-slate-500">{data.selected_label}</div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Target</div><div className="font-mono font-bold text-lg">{fmtINR(data.collection_target, { short: true })}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Actual</div><div className="font-mono font-bold text-lg">{fmtINR(data.collection_actual, { short: true })}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">% Achieved</div><div className="font-mono font-bold text-lg text-gold">{fmtPct(data.collection_pct)}</div></div>
          </div>
        </div>

        {/* Project-wise table */}
        <div className="bg-white rounded-md border border-slate-200">
          <div className="px-4 py-3 border-b border-slate-200 text-sm font-semibold">Project-wise Target vs Actual · {data.selected_label}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="project-target-actual-table">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-2">Project</th>
                  <th className="text-right px-4 py-2">Target</th>
                  <th className="text-right px-4 py-2">Actual</th>
                  <th className="text-right px-4 py-2">Achievement</th>
                </tr>
              </thead>
              <tbody>
                {data.project_target_actual.map((r) => (
                  <tr key={r.project_name} className="border-t border-slate-100">
                    <td className="px-4 py-2 truncate max-w-[220px]">{r.project_name}</td>
                    <td className="numeric-cell px-4 py-2">{fmtINR(r.target, { short: true })}</td>
                    <td className="numeric-cell px-4 py-2">{fmtINR(r.actual, { short: true })}</td>
                    <td className={`numeric-cell px-4 py-2 font-semibold ${r.achievement_pct >= 100 ? "text-emerald-600" : r.achievement_pct >= 70 ? "text-amber-600" : "text-red-600"}`}>
                      {fmtPct(r.achievement_pct)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-md border border-slate-200 p-4">
          <div className="text-sm font-semibold mb-3">Project Target vs Actual</div>
          <ResponsiveContainer width="100%" height={Math.max(280, data.project_target_actual.length * 28)}>
            <BarChart data={data.project_target_actual} layout="vertical" margin={{ left: 100 }}>
              <CartesianGrid stroke="#e2e8f0" />
              <XAxis type="number" tickFormatter={(v) => `${(v / 1e7).toFixed(0)}Cr`} tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="project_name" width={140} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => fmtINR(v, { short: true })} />
              <Bar dataKey="target" fill="#475569" name="Target" />
              <Bar dataKey="actual" fill="#D4AF37" name="Actual" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* FY Timeline */}
        <div className="bg-white rounded-md border border-slate-200">
          <div className="px-4 py-3 border-b border-slate-200 text-sm font-semibold">FY Monthly Carry-Forward Timeline</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="carry-forward-timeline">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2">Month</th>
                  <th className="text-right px-3 py-2">Base Target</th>
                  <th className="text-right px-3 py-2">Carry-in</th>
                  <th className="text-right px-3 py-2">Effective</th>
                  <th className="text-right px-3 py-2">Actual</th>
                  <th className="text-right px-3 py-2">Deficit</th>
                  <th className="text-right px-3 py-2">Achievement</th>
                </tr>
              </thead>
              <tbody>
                {data.timeline.map((row) => (
                  <tr key={row.period} className={`border-t border-slate-100 ${row.is_future ? "opacity-40" : ""}`}>
                    <td className="px-3 py-2 font-mono">{row.label}</td>
                    <td className="numeric-cell px-3 py-2">{fmtINR(row.base_target, { short: true })}</td>
                    <td className="numeric-cell px-3 py-2 text-amber-600">{fmtINR(row.carry_in, { short: true })}</td>
                    <td className="numeric-cell px-3 py-2 font-semibold">{fmtINR(row.effective_target, { short: true })}</td>
                    <td className="numeric-cell px-3 py-2">{fmtINR(row.actual, { short: true })}</td>
                    <td className="numeric-cell px-3 py-2 text-red-600">{fmtINR(row.deficit, { short: true })}</td>
                    <td className={`numeric-cell px-3 py-2 font-semibold ${row.achievement_pct >= 100 ? "text-emerald-600" : ""}`}>
                      {fmtPct(row.achievement_pct)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Sales Targets Monthly Matrix */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-sm font-semibold">Sales Targets · Monthly Matrix</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">
                {canEdit ? "Tap any cell to edit · ₹ Crore + unit count" : "Admin/Manager only · view-only"}
              </div>
            </div>
          </div>
          <div className="matrix-wrap" data-testid="sales-targets-matrix">
            <table>
              <colgroup>
                <col style={{ width: 220 }} />
                {monthsList.map((m) => <col key={m} style={{ width: 130 }} />)}
                <col style={{ width: 110 }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="sticky-col text-left">Project</th>
                  {monthsList.map((m) => (
                    <th key={m} className="numeric-cell text-[10px] uppercase tracking-wider">
                      {new Date(m).toLocaleString("en-US", { month: "short", year: "2-digit" })}
                    </th>
                  ))}
                  <th className="numeric-cell">Total</th>
                </tr>
              </thead>
              <tbody>
                {matrix && matrix.projects.map((p) => {
                  let rowTotal = 0;
                  return (
                    <tr key={p.project_name}>
                      <td className="sticky-col truncate">{p.project_name}</td>
                      {monthsList.map((m) => {
                        const t = matrix.targets.find((x) => x.project_name === p.project_name && x.period === m);
                        const key = `${p.project_name}|${m}`;
                        const isEditing = !!edit[key];
                        const val = t?.target_value || 0;
                        const cnt = t?.target_count || 0;
                        rowTotal += val;
                        return (
                          <td key={m} className="numeric-cell">
                            {isEditing ? (
                              <div className="flex flex-col gap-1">
                                <input
                                  className="w-20 px-1 py-0.5 border border-slate-300 rounded text-right font-mono text-xs"
                                  value={draft[key] ?? val}
                                  onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
                                  onKeyDown={(e) => { if (e.key === "Enter") saveCell(p.project_name, m); }}
                                  data-testid={`sales-target-input-${p.project_name}-${m}`}
                                  autoFocus
                                />
                                <input
                                  className="w-20 px-1 py-0.5 border border-slate-300 rounded text-right font-mono text-[10px]"
                                  placeholder="count"
                                  value={draft[key + "_c"] ?? cnt}
                                  onChange={(e) => setDraft({ ...draft, [key + "_c"]: e.target.value })}
                                />
                                <div className="flex gap-1 justify-end">
                                  <button onClick={() => saveCell(p.project_name, m)} className="text-emerald-600" data-testid={`save-sales-${p.project_name}-${m}`}>
                                    <Check size={12} />
                                  </button>
                                  <button onClick={() => setEdit({ ...edit, [key]: false })} className="text-red-600">
                                    <X size={12} />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => canEdit && setEdit({ ...edit, [key]: true })}
                                disabled={!canEdit}
                                className={`w-full text-right ${canEdit ? "cursor-pointer hover:bg-amber-50 rounded px-1 -mx-1" : "cursor-default"}`}
                                data-testid={`edit-sales-${p.project_name}-${m}`}
                              >
                                <div className="leading-tight">{val.toFixed(1)}</div>
                                <div className="leading-tight text-[9px] text-slate-400">{cnt}u</div>
                              </button>
                            )}
                          </td>
                        );
                      })}
                      <td className="numeric-cell font-semibold">{rowTotal.toFixed(1)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                {matrix && (
                <tr>
                  <td className="sticky-col">Total</td>
                  {monthsList.map((m) => {
                    const total = matrix.targets.filter((t) => t.period === m).reduce((a, b) => a + b.target_value, 0);
                    return <td key={m} className="numeric-cell">{total.toFixed(1)}</td>;
                  })}
                  <td className="numeric-cell">
                    {matrix.targets.reduce((a, b) => a + b.target_value, 0).toFixed(1)}
                  </td>
                </tr>
                )}
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
